/**
 * EditMealServingsButton - Button component for editing meal servings and participants
 *
 * This component displays serving information (participants, leftovers, cook time, dishes)
 * and triggers the EditMealServings modal when clicked (if editable).
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isScalable, getParticipantsForSpecificMeal as getParticipantsForSpecificMealFromPlan } from '@utils/Meals';
import { toHuman } from '@utils/Duration';
import { calculateTotalServingsForParticipants, calculateTotalYield, getBaseServings } from '@utils/servingsCalculations';

const EditMealServingsButton = ({
    primary,
    sortedMeals,
    profile,
    plan,
    recipes,
    onEditMealServings
}) => {
    // Calculate serving data for the button
    const servingData = useMemo(() => {
        // Check if the meal is scalable
        const canEdit = isScalable(primary);

        // Check if this is a leftover meal
        const isLeftoverMeal = sortedMeals.some(meal => meal.meal_type === 'leftover');

        // Calculate participants for this meal
        const participants = getParticipantsForSpecificMealFromPlan(primary.primary, profile);
        const participantCount = participants.length;

        // Calculate leftover servings as total yield minus current meal servings
        const isMultiDish = sortedMeals.length > 1;
        let totalLeftoverServings = 0;
        let leftoverDishBreakdown = [];

        sortedMeals.forEach((meal, dishIndex) => {
            const dishRecipe = recipes[meal.recipe_uuid] || recipes[meal.food_uuid];
            const baseServings = getBaseServings(dishRecipe);
            const dishYield = calculateTotalYield(meal.scaling || 1, baseServings);
            const dishCurrentMealServings = calculateTotalServingsForParticipants(participants, meal, dishRecipe);
            const dishLeftoverServings = dishYield - dishCurrentMealServings;

            if (dishLeftoverServings > 0) {
                if (isMultiDish) {
                    leftoverDishBreakdown.push({
                        dishNumber: dishIndex + 1,
                        dishName: dishRecipe?.title || dishRecipe?.name || `Dish ${dishIndex + 1}`,
                        servings: dishLeftoverServings
                    });
                }
                totalLeftoverServings += dishLeftoverServings;
            }
        });

        // Calculate maximum total_time from all meals
        let totalTime = 0;
        sortedMeals.forEach((meal) => {
            const recipe = recipes[meal.recipe_uuid];
            if (recipe && recipe.total_time && recipe.total_time > totalTime) {
                totalTime = recipe.total_time;
            }
        });

        return {
            canEdit,
            isLeftoverMeal,
            participantCount,
            totalLeftoverServings,
            leftoverDishBreakdown,
            totalTime,
            isMultiDish
        };
    }, [primary, sortedMeals, profile, plan.items, recipes]);

    const handleClick = () => {
        if (!servingData.canEdit) {
            return;
        }
        onEditMealServings();
    };

    const { canEdit, isLeftoverMeal, participantCount, totalLeftoverServings, leftoverDishBreakdown, totalTime, isMultiDish } = servingData;

    // Helper for conditional CSS classes
    const getSectionClassName = (baseClass) =>
        `${baseClass}${!canEdit ? ' disabled' : ''}`;

    return (
        <div className="edit-meal-servings" onClick={handleClick}>
            <section className="serves">
                <div>
                    <div>{isLeftoverMeal ? 'Leftover Meal' : 'Serves'}</div>
                    <div className="servings">{participantCount} {participantCount === 1 ? 'person' : 'people'}</div>
                </div>

                {!isLeftoverMeal && totalLeftoverServings > 0 ? <div className="plus">+</div> : null}

                {!isLeftoverMeal && totalLeftoverServings > 0 ? (
                    <div>
                        <div>Leftovers</div>
                        {isMultiDish && leftoverDishBreakdown.length > 0 ? (
                            <div className="servings leftover-breakdown">
                                {leftoverDishBreakdown.map((dish, index) => (
                                    <div key={index}>Dish {dish.dishNumber}: {dish.servings} {dish.servings === 1 ? 'serving' : 'servings'}</div>
                                ))}
                            </div>
                        ) : (
                            <div className="servings">{totalLeftoverServings} {totalLeftoverServings === 1 ? 'serving' : 'servings'}</div>
                        )}
                    </div>
                ) : null}

                <div className="edit">EDIT</div>
            </section>
            <div className="timings">
                <span className="stats-text">
                    <p className="dist-count">{sortedMeals.length} DISH{sortedMeals.length !== 1 ? 'ES' : ''}</p>
                    {totalTime > 0 && (
                        <p>
                            {toHuman(totalTime, true)}
                            <br />
                            PREP/COOK
                        </p>
                    )}
                </span>
            </div>
        </div>
    );
};

EditMealServingsButton.propTypes = {
    primary: PropTypes.object.isRequired,
    sortedMeals: PropTypes.array.isRequired,
    profile: PropTypes.object.isRequired,
    plan: PropTypes.object.isRequired,
    recipes: PropTypes.object.isRequired,
    onEditMealServings: PropTypes.func.isRequired,
};

export default EditMealServingsButton;
